import { Link } from '@tanstack/react-router'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { API_BASE_URL } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { TableCell, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { CreatorWithTier } from '@/lib/api/modules/creators'
import {
  useUpdateCreator,
  useEnrichCreatorInstagram,
  useSyncCreatorCoupon,
} from '@/hooks/use-creators'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import {
  MoreHorizontal,
  Copy,
  ExternalLink,
  PlayCircle,
  PauseCircle,
  RefreshCw,
  Instagram,
  AlertTriangle,
  Clock,
  MessageCircle,
  Loader2,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'

const STATUS_DOT_COLORS: Record<string, string> = {
  prospecting: 'bg-purple-500',
  negotiating: 'bg-blue-500',
  active: 'bg-green-500',
  paused: 'bg-yellow-500',
  churned: 'bg-red-500',
}

const STATUS_LABELS: Record<string, string> = {
  prospecting: 'Prospecção',
  negotiating: 'Negociação',
  active: 'Ativo',
  paused: 'Pausado',
  churned: 'Churn',
}

const CONTRACT_LABELS: Record<string, string> = {
  commission: 'Comissão',
  barter: 'Permuta',
  fixed: 'Fixo',
  fixed_plus_commission: 'Fixo + Comissão',
}

const TYPE_LABELS: Record<string, string> = {
  ugc_creator: 'UGC',
  internal: 'Interno',
}

function formatFollowers(count: number | null): string {
  if (!count) return '-'
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(count >= 10_000 ? 0 : 1)}K`
  return count.toString()
}

function formatTimeAgo(date: string | null): string | null {
  if (!date) return null
  return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR })
    .replace('aproximadamente ', '~')
    .replace('cerca de ', '~')
}

function isStale(date: string | null, days: number): boolean {
  if (!date) return true
  const diff = Date.now() - new Date(date).getTime()
  return diff > days * 24 * 60 * 60 * 1000
}

function formatContractDetail(creator: CreatorWithTier): string {
  const rate = creator.commissionTier
    ? `${(creator.commissionTier.commissionRate * 100).toFixed(0)}%`
    : null
  const fixed = creator.fixedValue ? `R$ ${(creator.fixedValue / 100).toFixed(0)}` : null

  switch (creator.contractType) {
    case 'commission':
      return rate ? `${rate} comissão` : 'Comissão'
    case 'fixed':
      return fixed ? `${fixed} fixo` : 'Fixo'
    case 'fixed_plus_commission':
      return [fixed, rate].filter(Boolean).join(' + ') || 'Fixo + Comissão'
    case 'barter':
      return 'Permuta'
    default:
      return CONTRACT_LABELS[creator.contractType] || creator.contractType
  }
}

interface CreatorRowProps {
  creator: CreatorWithTier
  selected?: boolean
  onSelectChange?: (checked: boolean) => void
}

export function CreatorRow({ creator, selected, onSelectChange }: CreatorRowProps) {
  const updateCreator = useUpdateCreator()
  const enrichInstagram = useEnrichCreatorInstagram()
  const syncCoupon = useSyncCreatorCoupon()

  const initials = creator.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()

  const isActive = creator.status === 'active'
  const hasCouponError = creator.couponSyncStatus === 'error'
  const noRecentPost = isActive && isStale(creator.lastPostAt, 30)
  const noRecentContact = isActive && isStale(creator.lastContactAt, 60)
  const hasAlerts = hasCouponError || noRecentPost || noRecentContact

  const totalFollowers = (creator.instagramFollowers || 0) + (creator.tiktokFollowers || 0)
  const platforms: string[] = []
  if (creator.instagramFollowers) platforms.push('IG')
  if (creator.tiktokFollowers) platforms.push('TT')

  const handleToggleStatus = () => {
    const newStatus = isActive ? 'paused' : 'active'
    updateCreator.mutate({ id: creator.id, data: { status: newStatus } })
  }

  const handleCopyCoupon = () => {
    if (creator.couponCode) {
      navigator.clipboard.writeText(creator.couponCode)
      toast.success('Cupom copiado!')
    }
  }

  const handleEnrichInstagram = () => {
    enrichInstagram.mutate(creator.id, {
      onSuccess: () => toast.success('Instagram atualizado!'),
      onError: (err) => toast.error('Erro ao enriquecer Instagram', { description: err.message }),
    })
  }

  const handleSyncCoupon = () => {
    syncCoupon.mutate(creator.id)
  }

  return (
    <TableRow className="group">
      {/* Checkbox */}
      <TableCell className="w-10 pr-0">
        <Checkbox
          checked={selected}
          onCheckedChange={(checked) => onSelectChange?.(checked === true)}
          aria-label={`Selecionar ${creator.name}`}
        />
      </TableCell>

      {/* Creator: avatar + name + handles + type badge */}
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-9 w-9">
              <AvatarImage
                src={`${API_BASE_URL}/api/admin/creators/${creator.id}/avatar?v=${creator.updatedAt}`}
                alt={creator.name}
              />
              <AvatarFallback className="text-sm bg-primary/10 text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
            {/* Status dot on avatar */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background',
                    STATUS_DOT_COLORS[creator.status] || 'bg-gray-400'
                  )}
                />
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">
                {STATUS_LABELS[creator.status] || creator.status}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5">
              <Link
                to="/creators/$id"
                params={{ id: creator.id }}
                search={{ tab: 'overview' }}
                className="font-medium text-foreground hover:underline truncate"
              >
                {creator.name}
              </Link>
              {creator.type !== 'creator' && TYPE_LABELS[creator.type] && (
                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 shrink-0">
                  {TYPE_LABELS[creator.type]}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {creator.instagramUsername && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={`https://instagram.com/${creator.instagramUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-0.5 hover:text-foreground transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Instagram className="h-3 w-3 text-pink-500 shrink-0" />
                      <span>@{creator.instagramUsername}</span>
                    </a>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Ver no Instagram</TooltipContent>
                </Tooltip>
              )}
              {creator.instagramUsername && creator.tiktokUsername && <span>·</span>}
              {creator.tiktokUsername && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={`https://tiktok.com/@${creator.tiktokUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-0.5 hover:text-foreground transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="flex h-3 w-3 shrink-0 items-center justify-center rounded-sm bg-black text-[6px] font-bold text-white dark:bg-white dark:text-black">
                        TK
                      </span>
                      <span>@{creator.tiktokUsername}</span>
                    </a>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Ver no TikTok</TooltipContent>
                </Tooltip>
              )}
              {!creator.instagramUsername && !creator.tiktokUsername && (
                <span className="italic">Sem rede social</span>
              )}
            </div>
          </div>
        </div>
      </TableCell>

      {/* Audience */}
      <TableCell className="hidden lg:table-cell">
        {totalFollowers > 0 ? (
          <div className="flex flex-col">
            <span className="font-medium tabular-nums">{formatFollowers(totalFollowers)}</span>
            <span className="text-xs text-muted-foreground">{platforms.join(' · ')}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Tier & Contract */}
      <TableCell className="hidden md:table-cell">
        <div className="flex flex-col">
          <span className="font-medium">{creator.commissionTier?.name || 'Sem Tier'}</span>
          <span className="text-xs text-muted-foreground">{formatContractDetail(creator)}</span>
        </div>
      </TableCell>

      {/* Coupon & Program */}
      <TableCell className="hidden lg:table-cell">
        {creator.couponCode ? (
          <div className="flex flex-col gap-0.5">
            <button
              onClick={handleCopyCoupon}
              className="font-mono text-xs hover:text-primary transition-colors group/coupon flex items-center gap-1 text-left"
            >
              <span className="truncate max-w-25">{creator.couponCode}</span>
              <Copy className="h-3 w-3 opacity-0 group-hover/coupon:opacity-100 transition-opacity shrink-0" />
              {creator.couponSyncStatus === 'synced' && (
                <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
              )}
              {creator.couponSyncStatus === 'error' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle className="h-3 w-3 text-destructive shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="text-xs max-w-64">
                    {creator.couponSyncError || 'Erro de sincronização'}
                  </TooltipContent>
                </Tooltip>
              )}
            </button>
            {creator.program && (
              <span className="text-[11px] text-muted-foreground truncate max-w-30">
                {creator.program.name}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Receita */}
      <TableCell className="hidden xl:table-cell">
        {creator.totalSalesAmount > 0 ? (
          <div className="flex flex-col">
            <span className="font-medium tabular-nums text-sm">
              {formatCurrency(creator.totalSalesAmount)}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {creator.totalSalesCount} venda{creator.totalSalesCount !== 1 ? 's' : ''} ·{' '}
              {formatCurrency(creator.totalCommission)} com.
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Activity */}
      <TableCell className="hidden xl:table-cell">
        <div className="flex flex-col gap-0.5 text-xs">
          <span className={cn(noRecentPost && 'text-amber-600 dark:text-amber-400')}>
            {creator.lastPostAt
              ? `Post ${formatTimeAgo(creator.lastPostAt)}`
              : isActive
                ? 'Sem posts'
                : '-'}
          </span>
          <span
            className={cn(
              'text-muted-foreground',
              noRecentContact && 'text-amber-600 dark:text-amber-400'
            )}
          >
            {creator.lastContactAt
              ? `Contato ${formatTimeAgo(creator.lastContactAt)}`
              : isActive
                ? 'Sem contato'
                : '-'}
          </span>
        </div>
      </TableCell>

      {/* Alerts */}
      <TableCell className="w-10 hidden sm:table-cell">
        {hasAlerts ? (
          <div className="flex items-center gap-1">
            {hasCouponError && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                </TooltipTrigger>
                <TooltipContent className="text-xs">Cupom com erro de sync</TooltipContent>
              </Tooltip>
            )}
            {noRecentPost && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Clock className="h-3.5 w-3.5 text-amber-500" />
                </TooltipTrigger>
                <TooltipContent className="text-xs">Sem posts há 30+ dias</TooltipContent>
              </Tooltip>
            )}
            {noRecentContact && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <MessageCircle className="h-3.5 w-3.5 text-amber-500" />
                </TooltipTrigger>
                <TooltipContent className="text-xs">Sem contato há 60+ dias</TooltipContent>
              </Tooltip>
            )}
          </div>
        ) : null}
      </TableCell>

      {/* Actions */}
      <TableCell className="w-10 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Ações</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem asChild>
              <Link to="/creators/$id" params={{ id: creator.id }} search={{ tab: 'overview' }}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Ver detalhes
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={handleToggleStatus} disabled={updateCreator.isPending}>
              {updateCreator.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : isActive ? (
                <PauseCircle className="mr-2 h-4 w-4" />
              ) : (
                <PlayCircle className="mr-2 h-4 w-4" />
              )}
              {isActive ? 'Pausar' : 'Ativar'}
            </DropdownMenuItem>

            {creator.couponCode && (
              <DropdownMenuItem onClick={handleCopyCoupon}>
                <Copy className="mr-2 h-4 w-4" />
                Copiar cupom
              </DropdownMenuItem>
            )}

            {creator.instagramUsername && (
              <DropdownMenuItem
                onClick={handleEnrichInstagram}
                disabled={enrichInstagram.isPending}
              >
                {enrichInstagram.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Instagram className="mr-2 h-4 w-4" />
                )}
                Atualizar Instagram
              </DropdownMenuItem>
            )}

            {creator.couponCode && creator.programId && (
              <DropdownMenuItem onClick={handleSyncCoupon} disabled={syncCoupon.isPending}>
                {syncCoupon.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Sincronizar cupom
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}
